import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import DateInput from '../common/DateInput';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import FormDialog from '../common/FormDialog';
import CompanyForm from '../Companies/CompanyForm';
import { driversApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';

const EMPTY = {
    id: 0, firstName: '', lastName: '', birthDate: null, birthPlace: '', birthCountry: '', uniqueId: '',
    workStart: null, workEnd: null, company: null, turistika: false,
    cardId: 0, cardNumber: '', issuingAuthorityName: '', issueDate: null, expiryDate: null,
};

export default function DriverForm({ id = 0, onClose, onSaved }) {
    const alerts = useAlerts();
    const companies = useSelector((s) => s.companies.list);
    const [f, setF] = useState(EMPTY);
    const [busy, setBusy] = useState(!!id);
    const [dirty, setDirty] = useState(false);
    const [title, setTitle] = useState(id ? '' : 'Novi vozač');
    const [companyForm, setCompanyForm] = useState(false);

    useEffect(() => {
        if (!id) return;
        driversApi.get(id).then((d) => {
            const c = d.card || {};
            setF({
                ...EMPTY, ...d,
                cardId: c.id || 0, cardNumber: c.cardNumber || '', issuingAuthorityName: c.issuingAuthorityName || '',
                issueDate: c.issueDate || null, expiryDate: c.expiryDate || null,
            });
            setTitle(`Vozač:  ${d.firstName} ${d.lastName}`);
        }).catch(onClose).finally(() => setBusy(false));
    }, [id]);

    const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target ? e.target.value : e.value })); setDirty(true); };

    const hasCard = !!f.cardNumber.trim();
    const errs = {
        firstName: !f.firstName.trim(), lastName: !f.lastName.trim(), birthDate: !f.birthDate,
        birthPlace: !f.birthPlace.trim(), birthCountry: !f.birthCountry.trim(), uniqueId: !f.uniqueId.trim(),
        workStart: !f.workStart, issueDate: hasCard && !f.issueDate, expiryDate: hasCard && !f.expiryDate,
    };
    const valid = !Object.values(errs).some(Boolean);
    /* kao u staroj aplikaciji: neispravno polje se označava, a Sačuvaj je onemogućen dok forma nije ispravna */
    const inv = (k) => (dirty && errs[k] ? 'p-invalid' : '');

    const save = async () => {
        if (!valid) return;
        const body = {
            id: f.id, firstName: f.firstName, lastName: f.lastName, birthDate: f.birthDate, birthPlace: f.birthPlace,
            birthCountry: f.birthCountry, uniqueId: f.uniqueId, turistika: !!f.turistika,
            workStart: f.workStart, workEnd: f.workEnd,
            company: f.company && f.company.id != null ? f.company : null,
            card: hasCard ? {
                id: f.cardId, cardNumber: f.cardNumber, validityBegin: f.issueDate, issuingMemberState: '',
                issuingAuthorityName: f.issuingAuthorityName, issueDate: f.issueDate, expiryDate: f.expiryDate,
            } : null,
        };
        try {
            f.id ? await driversApi.update(body) : await driversApi.create(body);
            alerts.success(f.id ? 'Ažuriranje vozača uspešno' : 'Dodavanje vozača uspešno');
            if (onSaved) await onSaved();
            onClose();
        } catch { /* toast */ }
    };

    /* obavezno: uvek osnovni podaci; datumi kartice samo kad je unet broj kartice */
    const required = (k) => ['firstName', 'lastName', 'birthDate', 'birthPlace', 'birthCountry', 'uniqueId', 'workStart'].includes(k)
        || (hasCard && ['issueDate', 'expiryDate'].includes(k));
    const lbl = (k, label) => (
        <label htmlFor={`drv-${k}`}>{label}{required(k) && <span className="req" aria-hidden="true">*</span>}</label>
    );
    const text = (k, label) => (
        <div className="field">
            {lbl(k, label)}
            <InputText id={`drv-${k}`} value={f[k] || ''} onChange={set(k)} className={inv(k)} aria-required={required(k) || undefined} />
        </div>
    );
    const cal = (k, label, range) => (
        <div className="field">
            {lbl(k, label)}
            <DateInput inputId={`drv-${k}`} value={f[k]} onChange={set(k)} dateFormat="dd.mm.yy" showIcon monthNavigator yearNavigator yearRange={range} className={inv(k)} />
        </div>
    );

    return (
        <>
            <FormDialog visible title={title} onClose={onClose} onSave={save}
                dirty={dirty} canSave={valid && dirty} busy={busy} width="56rem">
                <div className="form-section">
                    <h4 className="form-section-title">Vozač</h4>
                    <div className="form-grid">
                        {text('firstName', 'Ime')}
                        {text('lastName', 'Prezime')}
                        {text('uniqueId', 'JMBG')}
                        {cal('birthDate', 'Datum rođenja', '1930:2010')}
                        {text('birthPlace', 'Mesto rođenja')}
                        {text('birthCountry', 'Država rođenja')}
                        {cal('workStart', 'Radi od', '2010:2030')}
                        {cal('workEnd', 'Radi do', '2010:2030')}
                        {/* Turistika = propisi za kazne: AETR i R Srbije međugradski; isključeno = R Srbije gradski (umesto br. lične karte / pasoša) */}
                        <div className="field">
                            <label htmlFor="drv-turistika">Turistika</label>
                            <div className="check">
                                <Checkbox inputId="drv-turistika" checked={!!f.turistika}
                                    onChange={(e) => { setF((x) => ({ ...x, turistika: e.checked })); setDirty(true); }} />
                                <span className="muted">{f.turistika ? 'AETR i međugradski propis' : 'Gradski propis'}</span>
                            </div>
                        </div>
                        <div className="field span-all">
                            <label htmlFor="drv-company">Kompanija</label>
                            <div className="row-inline">
                                <Dropdown inputId="drv-company" value={companies.find((c) => (f.company ? c.id === f.company.id : c.id == null)) || null}
                                    options={companies} optionLabel="name" dataKey="id" filter emptyFilterMessage="Nema rezultata"
                                    onChange={(e) => { setF((x) => ({ ...x, company: e.value })); setDirty(true); }} />
                                <Button type="button" label="Dodaj kompaniju" icon="pi pi-plus" text onClick={() => setCompanyForm(true)} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="form-section">
                    <h4 className="form-section-title">Kartica</h4>
                    <div className="form-grid">
                        {text('cardNumber', 'Broj kartice')}
                        {text('issuingAuthorityName', 'Organ izdavanja')}
                        {cal('issueDate', 'Datum izdavanja', '2010:2040')}
                        {cal('expiryDate', 'Datum isteka', '2010:2040')}
                    </div>
                </div>
            </FormDialog>
            {companyForm && <CompanyForm onClose={() => setCompanyForm(false)} />}
        </>
    );
}
